import { css } from "@emotion/react";
import type { ImmutableObject } from "@/lib/immutable";
import type { PackageItem, Product } from "../../../hooks/useInventoryState";
import { useShoppingCartItemsState } from "../../../hooks/useShoppingCartItemsState";
import { useTranslate } from "../../../hooks/useTranslate/useTranslate";
import FwFunc from "../../../lib/FwReact/FwFunc";
import { FwNumberDropDown } from "../../../lib/FwReact/FwNumberDropDown";
import { ShoppingCartPackageItemAvailability } from "../PackageItemFields/ShoppingCartPackageItemAvailability";
import { ShoppingCartPackageItemImage } from "../PackageItemFields/ShoppingCartPackageItemImage";
import type { ShoppingCartViewSettings } from "../ShoppingCartControl";

//------------------------------------------------------------------------------------
export type ShoppingCartPackageItemOptionalDesktopProps = {
	packageProduct: ImmutableObject<Product>;
	packageItem: ImmutableObject<PackageItem>;
	enableQty: boolean;
	viewSettings: ShoppingCartViewSettings;
};
export const ShoppingCartPackageItemOptionalDesktop: React.FC<
	ShoppingCartPackageItemOptionalDesktopProps
> = (props) => {
	const translate = useTranslate();
	const packageProduct = props.packageProduct;
	const isAvailable = (packageProduct.Availability?.TotalIn ?? 0) > 0;
	const canAddToCart =
		props.enableQty &&
		(isAvailable || props.viewSettings.allowAddingUnavailableItems);
	const item = props.packageItem;
	const shoppingCartItemsState = useShoppingCartItemsState();
	const showAvailability = item.Availability !== undefined;
	const showDailyRate =
		props.viewSettings.hasRates &&
		props.viewSettings.hasDailyRate;
	const showWeeklyRate =
		props.viewSettings.hasRates &&
		props.viewSettings.hasWeeklyRate;
	const showMonthlyRate =
		props.viewSettings.hasRates &&
		props.viewSettings.hasMonthlyRate;

	const handleChangeQtyAsync = async (value: number): Promise<void> => {
		return Promise.resolve().then(() => {
			shoppingCartItemsState.setPackageItemQty(
				props.packageProduct,
				props.packageItem,
				value,
			);
		});
	};
	const maxQty = shoppingCartItemsState.getMaxPackageItemQty(
		packageProduct,
		props.packageItem,
	);
	return (
		<tr className="packageitemrow">
			<td className="col-legend"></td>
			<td className="col-image" data-label={translate("Image")}>
				<ShoppingCartPackageItemImage
					product={packageProduct}
					packageItem={item}
				/>
			</td>
			<td className="col-description" data-label={translate("Item")}>
				{item.Description}
			</td>
			{(canAddToCart || showAvailability) && (
				<td className="col-qty" data-label={translate("Quantity")}>
					<div className="mobile-field-value">
						{canAddToCart && (
							<FwNumberDropDown
								css={css`flex:1 1 0;`}
								value={shoppingCartItemsState.getPackageItemQty(
									props.packageProduct,
									item,
								)}
								maxQty={maxQty}
								onChange={(newValue) => {
									handleChangeQtyAsync(newValue);
								}}
							/>
						)}
						{showAvailability && (
							<ShoppingCartPackageItemAvailability packageItem={item} />
						)}
					</div>
				</td>
			)}

			{showDailyRate && (
				<td className="col-dailyrate" data-label={translate("Daily")}>
					<span className="mobile-field-value">
						<span className="CurrencySymbol">{item.CurrencySymbol}</span>
						<span className="Price">
							{FwFunc.numberFormatter(item.DailyRate, 2, ",", ".")}
						</span>
					</span>
				</td>
			)}

			{showWeeklyRate && (
				<td className="col-weeklyrate" data-label={translate("Weekly")}>
					<span className="mobile-field-value">
						<span className="CurrencySymbol">{item.CurrencySymbol}</span>
						<span className="Price">
							{FwFunc.numberFormatter(item.WeeklyRate, 2, ",", ".")}
						</span>
					</span>
				</td>
			)}

			{showMonthlyRate && (
				<td className="col-monthlyrate" data-label={translate("Monthly")}>
					<span className="mobile-field-value">
						<span className="CurrencySymbol">{item.CurrencySymbol}</span>
						<span className="Price">
							{FwFunc.numberFormatter(item.MonthlyRate, 2, ",", ".")}
						</span>
					</span>
				</td>
			)}

			{false && showAvailability && (
				<td className="col-availability">
					<ShoppingCartPackageItemAvailability packageItem={item} />
				</td>
			)}
		</tr>
	);
};
